import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../axios/axiosInstance";
import { containsProfanity } from "../../../validation/profanityFilter";

// Single-word username: at least 2 unicode letters, optionally followed by
// digits (e.g. "Mohamed", "John99"). No spaces or symbols. Mirrors the Yup +
// backend validators exactly. Kept in sync with signUpValidation.js.
const USERNAME_FORMAT = /^\p{L}{2,}\d*$/u;

/**
 * Live-checks a username against the backend while the user types.
 * Returns one of:
 *   "idle"        — empty input, no message
 *   "too_short"   — < 3 chars
 *   "too_long"    — > 30 chars
 *   "invalid_format" — has spaces/symbols, or starts with a digit, or fewer than 2 letters
 *   "inappropriate"  — matched a profanity/blocked word
 *   "checking"    — API call in flight
 *   "available"   — API says available
 *   "taken"       — API says the username exists
 *
 * The three "shape" checks (too_short / too_long / invalid_format /
 * inappropriate) short-circuit the API call so we don't spam the backend for
 * obviously-bad inputs.
 */
export const useUsernameAvailability = (username) => {
  const [status, setStatus] = useState("idle");
  const debounceRef = useRef(null);

  useEffect(() => {
    const value = (username || "").trim();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value) {
      setStatus("idle");
      return;
    }
    if (value.length < 3) {
      setStatus("too_short");
      return;
    }
    if (value.length > 30) {
      setStatus("too_long");
      return;
    }
    if (!USERNAME_FORMAT.test(value)) {
      setStatus("invalid_format");
      return;
    }
    if (containsProfanity(value)) {
      setStatus("inappropriate");
      return;
    }

    setStatus("checking");
    let cancelled = false;

    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get("/user/check-username", {
          params: { username: value },
          skipAuthLogout: true,
        });
        if (cancelled) return;
        if (data?.available) {
          setStatus("available");
        } else {
          // Trust the server's specific reason when it gave one; fall back to
          // generic "taken" for older backends.
          const reason = data?.reason;
          if (reason === "inappropriate") setStatus("inappropriate");
          else if (reason === "invalid_format") setStatus("invalid_format");
          else if (reason === "too_short") setStatus("too_short");
          else if (reason === "too_long") setStatus("too_long");
          else setStatus("taken");
        }
      } catch {
        if (!cancelled) setStatus("idle");
      }
    }, 450);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username]);

  return status;
};
